<template>
  <div class="risk-detail-container">
    <!-- Assessment Questions -->
    <div v-if="rowData.report_data && rowData.report_data.report_data && rowData.report_data.report_data.length > 0" class="questions-section">
      <div v-for="(item, index) in rowData.report_data.report_data" :key="item.question_id" class="question-card">
        <div class="question-header" @click="toggleQuestion(item.question_id)" :class="{ 'cursor-pointer': (item.answer === 'yes' && !item.has_sub_questions) || item.admin_comment || (item.has_sub_questions && item.sub_questions && item.sub_questions.length > 0) }">
          <div class="d-flex align-items-start">
            <span class="question-number">{{ index + 1 }}</span>
            <div style="flex-grow: 1;">
              <div class="question-text">{{ item.question_text }}</div>
              <div class="question-meta">
                <span class="answer-badge" :class="getAnswerBadgeClass(item.answer)">{{ formatAnswer(item.answer) }}</span>
                <span v-if="item.answer === 'yes' && !item.has_sub_questions && item.risk_level" class="risk-level-badge" :class="getRiskLevelBadgeClass(item.risk_level)">{{ getRiskLevelText(item.risk_level) }}</span>
                <span v-if="item.has_sub_questions && item.sub_questions && item.sub_questions.length > 0" class="badge badge-info ml-1">{{ item.sub_questions.length }} {{ $t('sub-questions') }}</span>
              </div>
            </div>
            <div v-if="(item.answer === 'yes' && !item.has_sub_questions) || item.admin_comment || (item.has_sub_questions && item.sub_questions && item.sub_questions.length > 0)" class="ml-2">
              <i class="fa" :class="expandedQuestions[item.question_id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
          </div>
        </div>

        
        
            
        <!-- Comments and Details -->
        <b-collapse :id="`collapse-${item.question_id}`" v-model="expandedQuestions[item.question_id]">
          <!-- Parent question details (match sub-question logic) -->
          <div v-if="item.answer === 'yes' || item.admin_comment" class="question-details">
            <div v-if="item.user_comment" class="detail-section user-comment">
              <div class="detail-label">{{ $t('Comment') }}</div>
              <div class="detail-content">{{ item.user_comment }}</div>
            </div>
            <div v-if="1" class="detail-section admin-comment">
              <div class="detail-label">
                {{ $t('Admin Comment') }}
                <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="editComment(item)">
                  <i class="fa fa-edit"></i>
                </button>
              </div>
              <div class="detail-content">{{ item.admin_comment }}</div>
            </div>

            <!-- Parent question images -->
            <div v-if="(item.images_user) || (item.images_admin)" class="detail-section images-section">
              <div v-if="item.images_user && item.images_user.length > 0" class="image-group">
                <div class="detail-label">{{ $t('Images') }} ({{ item.images_user.length }})</div>
                <div class="image-gallery">
                  <div v-for="(image, idx) in item.images_user" :key="`user-${item.question_id}-${image}-${idx}`" class="image-container">
                    <img :src="getImageUrl(image)" class="gallery-image" @click="openLightbox(image, 'user', item.question_id, idx)">
                    <button v-if="isAdmin" class="image-delete-btn" @click.stop="confirmDeleteImage(item, 'user', idx)" :title="$t('Delete Image')">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="item.images_admin" class="image-group">
                <div class="detail-label">
                  {{ $t('Admin Images') }} ({{ item.images_admin.length }})
                  <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="addImages(item)">
                    <i class="fa fa-plus"></i>
                  </button>
                </div>
                <div class="image-gallery">
                  <div v-for="(image, idx) in item.images_admin" :key="`admin-${item.question_id}-${image}-${idx}`" class="image-container">
                    <img :src="getImageUrl(image)" class="gallery-image" @click="openLightbox(image, 'admin', item.question_id, idx)">
                    <button v-if="isAdmin" class="image-delete-btn" @click.stop="confirmDeleteImage(item, 'admin', idx)" :title="$t('Delete Image')">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Admin actions for parent questions -->
            <div v-if="isAdmin && item.answer === 'yes' && item.enable_comment_image" class="admin-actions">
              <button v-if="!item.images_admin || item.images_admin.length === 0" class="btn btn-sm btn-outline-secondary" @click="addImages(item)">
                <i class="fa fa-image"></i> {{ $t('Add Images') }}
              </button>
            </div>
          </div>

          <!-- Sub-questions section -->
          <div v-if="item.has_sub_questions && item.sub_questions && item.sub_questions.length > 0" class="question-details">
            <div class="sub-questions-section">
              <div v-for="(subItem, subIndex) in item.sub_questions" :key="subItem.question_id" class="sub-question-card">
                <div class="sub-question-header">
                  <div class="d-flex align-items-start">
                    <span class="sub-question-number">{{ index + 1 }}.{{ subIndex + 1 }}</span>
                    <div style="flex-grow: 1;">
                      <div class="question-text">{{ subItem.question_text }}</div>
                      <div class="question-meta">
                        <span class="answer-badge" :class="getAnswerBadgeClass(subItem.answer)">{{ formatAnswer(subItem.answer) }}</span>
                        <span v-if="subItem.answer === 'yes' && subItem.risk_level" class="risk-level-badge" :class="getRiskLevelBadgeClass(subItem.risk_level)">{{ getRiskLevelText(subItem.risk_level) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Sub-question details -->
                <div v-if="subItem.answer === 'yes'" class="sub-question-details pt-2">
                  <div v-if="subItem.user_comment" class="detail-section user-comment">
                    <div class="detail-label">{{ $t('Comment') }}</div>
                    <div class="detail-content">{{ subItem.user_comment }}</div>
                  </div>
                  <div v-if="1" class="detail-section admin-comment">
                    <div class="detail-label">
                      {{ $t('Admin Comment') }}
                      <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="editComment(subItem)">
                        <i class="fa fa-edit"></i>
                      </button>
                    </div>
                    <div class="detail-content">{{ subItem.admin_comment }}</div>
                  </div>
                  
                  <!-- Sub-question images -->
                  <div v-if="(subItem.images_user ) || (subItem.images_admin )" class="detail-section images-section">
                    <div v-if="subItem.images_user && subItem.images_user.length > 0" class="image-group">
                      <div class="detail-label">{{ $t('Images') }} ({{ subItem.images_user.length }})</div>
                      <div class="image-gallery">
                        <div v-for="(image, idx) in subItem.images_user" :key="`user-${subItem.question_id}-${image}-${idx}`" class="image-container">
                          <img :src="getImageUrl(image)" class="gallery-image" @click="openLightbox(image, 'user', subItem.question_id, idx)">
                          <button v-if="isAdmin" class="image-delete-btn" @click.stop="confirmDeleteImage(subItem, 'user', idx)" :title="$t('Delete Image')">
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="subItem.images_admin" class="image-group">
                      <div class="detail-label">
                        {{ $t('Admin Images') }} ({{ subItem.images_admin.length }})
                        <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="addImages(subItem)">
                          <i class="fa fa-plus"></i>
                        </button>
                      </div>
                      <div class="image-gallery">
                        <div v-for="(image, idx) in subItem.images_admin" :key="`admin-${subItem.question_id}-${image}-${idx}`" class="image-container">
                          <img :src="getImageUrl(image)" class="gallery-image" @click="openLightbox(image, 'admin', subItem.question_id, idx)">
                          <button v-if="isAdmin" class="image-delete-btn" @click.stop="confirmDeleteImage(subItem, 'admin', idx)" :title="$t('Delete Image')">
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Admin actions for sub-questions -->
                  <div v-if="isAdmin && subItem.answer === 'yes' && subItem.enable_comment_image" class="admin-actions">
                    <button v-if="!subItem.images_admin || subItem.images_admin.length === 0" class="btn btn-sm btn-outline-secondary" @click="addImages(subItem)">
                      <i class="fa fa-image"></i> {{ $t('Add Images') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </b-collapse>
      </div>
    </div>

    <!-- Additional Observations Section -->
    <div v-if="rowData.report_data.additional_risk_item && (rowData.report_data.additional_risk_item.comment || (rowData.report_data.additional_risk_item.images && rowData.report_data.additional_risk_item.images.length > 0) || rowData.report_data.additional_risk_item.risk_level)" class="additional-risk-section mt-4">
      <div class="question-card">
        <div class="question-header">
          <div class="d-flex align-items-start">
            <span class="question-number"><i class="fa fa-exclamation-triangle"></i></span>
            <div style="flex-grow: 1;">
              <div class="question-text">{{ $t('Additional Observations') }}</div>
              <div v-if="rowData.report_data.additional_risk_item.risk_level" class="question-meta">
                <span class="risk-level-badge" :class="getRiskLevelBadgeClass(rowData.report_data.additional_risk_item.risk_level)">
                  {{ getRiskLevelText(rowData.report_data.additional_risk_item.risk_level) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="question-details mt-3">
          <div v-if="rowData.report_data.additional_risk_item.comment" class="detail-section user-comment">
            <div class="detail-label">
              {{ $t('Description / Comments') }}
              <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="editAdditionalRisk()">
                <i class="fa fa-edit"></i>
              </button>
            </div>
            <div class="detail-content">{{ rowData.report_data.additional_risk_item.comment }}</div>
          </div>

          <!-- Additional Observation Images -->
          <div v-if="rowData.report_data.additional_risk_item.images && rowData.report_data.additional_risk_item.images.length > 0" class="detail-section images-section">
            <div class="image-group">
              <div class="detail-label">
                {{ $t('Images') }} ({{ rowData.report_data.additional_risk_item.images.length }})
                <button v-if="isAdmin" class="btn btn-sm btn-link p-0 ml-2" @click="addAdditionalRiskImages()">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
              <div class="image-gallery">
                <div v-for="(image, idx) in rowData.report_data.additional_risk_item.images" :key="`additional-${idx}`" class="image-container">
                  <img :src="getImageUrl(image)" class="gallery-image" @click="openLightbox(image, 'additional', 'additional', idx)">
                  <button v-if="isAdmin" class="image-delete-btn" @click.stop="confirmDeleteAdditionalImage(idx)" :title="$t('Delete Image')">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Show admin edit button even when there's no content yet -->
          <div v-if="isAdmin && !rowData.report_data.additional_risk_item.comment && (!rowData.report_data.additional_risk_item.images || rowData.report_data.additional_risk_item.images.length === 0)" class="detail-section">
            <button class="btn btn-sm btn-outline-secondary" @click="editAdditionalRisk()">
              <i class="fa fa-edit"></i> {{ $t('Add Additional Observations') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Admin Tools Row -->
    <div v-if="isAdmin" class="row admin-tools-row">
      <div class="col-12">
        <div class="admin-tools-card">
          <div class="detail-label">{{ $t('Admin Actions') }}</div>
          <div class="admin-tools-grid">
            <button class="btn btn-sm btn-outline-secondary" @click="printReport">
              <i class="fa fa-print"></i> {{ $t('Print Report') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Comment Modal -->
    <b-modal v-model="showEditCommentModal" :title="$t('Edit Admin Comment')" @ok="saveComment" size="lg">
      <!-- Original User Comment -->
      <div v-if="editingComment.originalComment" class="form-group">
        <label class="font-weight-bold">{{ $t('Original User Comment') }}</label>
        <div class="p-2 border rounded bg-light">
          <small class="text-muted">{{ $t('Original comment (read-only)') }}:</small>
          <p class="mb-0">{{ editingComment.originalComment }}</p>
        </div>
      </div>
      
      <!-- Admin Comment -->
      <div class="form-group">
        <label for="adminComment" class="font-weight-bold">{{ $t('Admin Comment') }}</label>
        <b-form-textarea
          id="adminComment"
          v-model="editingComment.comment"
          rows="4"
          :placeholder="$t('Enter admin comment...')"
        ></b-form-textarea>
        <small class="text-muted">{{ $t('This comment will be added by admin and preserved alongside the original user comment.') }}</small>
      </div>
    </b-modal>


    <!-- Delete Image Confirmation Modal -->
    <b-modal
      v-model="showDeleteImageModal"
      :title="$t('Confirm Delete')"
      @ok="deleteImage"
      ok-variant="danger"
      :ok-title="$t('Delete')"
      :cancel-title="$t('Cancel')">
      <p>{{ $t('Are you sure you want to delete this image?') }}</p>
      <p class="text-muted">{{ $t('This action cannot be undone.') }}</p>
    </b-modal>



    <!-- Edit Additional Observations Modal -->
    <b-modal v-model="showEditAdditionalRiskModal" :title="$t('Edit Additional Observations')" @ok="saveAdditionalRisk">
      <div class="form-group">
        <label for="additionalObservationComment">{{ $t('Description / Comments') }}</label>
        <b-form-textarea
          id="additionalObservationComment"
          v-model="editingAdditionalRisk.comment"
          rows="4"
          :placeholder="$t('Describe any additional observations, risks, or concerns...')"
        ></b-form-textarea>
      </div>
      <div class="form-group mt-3">
        <label>{{ $t('Risk Level Assessment') }}</label>
        <small class="text-muted d-block mb-2">{{ $t('If you have identified a risk above, please assess its level') }}</small>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="additionalRiskLevel"
            id="additionalRiskNone"
            :value="null"
            v-model="editingAdditionalRisk.risk_level"
          >
          <label class="form-check-label" for="additionalRiskNone">
            {{ $t('No Additional Risk') }}
          </label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="additionalRiskLevel"
            id="additionalRiskMedium"
            value="medium"
            v-model="editingAdditionalRisk.risk_level"
          >
          <label class="form-check-label" for="additionalRiskMedium">
            {{ $t('Medium Risk') }}
          </label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="additionalRiskLevel"
            id="additionalRiskHigh"
            value="high"
            v-model="editingAdditionalRisk.risk_level"
          >
          <label class="form-check-label" for="additionalRiskHigh">
            {{ $t('High Risk') }}
          </label>
        </div>
      </div>
    </b-modal>

    <!-- Image Lightbox Modal -->
    <div v-if="lightboxVisible" class="lightbox-overlay" @click="closeLightbox">
      <div class="lightbox-content" @click.stop>
        <div class="lightbox-header">
          <h5 class="lightbox-title">{{ lightboxTitle }}</h5>
          <button class="lightbox-close" @click="closeLightbox">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="lightbox-body">
          <img :src="lightboxImage" class="lightbox-image" :alt="lightboxTitle">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'RiskReportsDetailRow',
  props: {
    rowData: {
      type: Object,
      required: true
    },
    rowIndex: {
      type: Number
    }
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'apiUrl',
      'uploadUrl'
    ]),
    isAdmin () {
      return this.currentUser && this.currentUser.role && this.currentUser.role.includes('admin')
    },
    riskLevelOptions () {
      return [
        { value: 'medium', text: this.$t('Medium Risk') },
        { value: 'high', text: this.$t('High Risk') }
      ]
    },
    answerOptions () {
      return [
        { value: 'no', text: this.$t('No') },
        { value: 'yes', text: this.$t('Yes') }
      ]
    }
  },
  data () {
    return {
      showEditCommentModal: false,
      showAddRiskRowModal: false,
      showEditAdditionalRiskModal: false,
      showDeleteImageModal: false,
      deleteImageData: {
        item: null,
        type: '',
        index: -1
      },
      editingComment: {
        questionId: null,
        comment: '',
        originalComment: ''
      },
      newRiskRow: {
        question_id: null,
        question_text: '',
        risk_level: 'medium',
        answer: 'no',
        admin_comment: ''
      },
      editingAdditionalRisk: {
        comment: '',
        risk_level: null,
        images: []
      },
      lightboxVisible: false,
      lightboxImage: '',
      lightboxTitle: '',
      expandedQuestions: {},
      showFullComments: false
    }
  },
  mounted () {
    // Initialize expanded state for questions with 'yes' answers or admin comments
    if (this.rowData.report_data && this.rowData.report_data.report_data) {
      this.rowData.report_data.report_data.forEach(item => {
        if (item.answer === 'yes' || item.admin_comment) {
          this.$set(this.expandedQuestions, item.question_id, false)
        }
      })
    }
  },
  methods: {
    toggleQuestion (questionId) {
      // Only toggle if the question has details to show
      const question = this.rowData.report_data.report_data.find(q => q.question_id === questionId)
      if (question && (question.answer === 'yes' || question.admin_comment)) {
        this.$set(this.expandedQuestions, questionId, !this.expandedQuestions[questionId])
      }
    },
    formatAnswer (answer) {
      const answers = {
        'yes': this.$t('Yes'),
        'no': this.$t('No'),
        'not_applicable': this.$t('Not Applicable')
      }
      return answers[answer] || answer
    },
    getAnswerClass (answer) {
      return {
        'text-danger font-weight-bold': answer === 'yes',
        'text-success font-weight-bold': answer === 'no',
        'text-muted': answer === 'not_applicable'
      }
    },
    getImageUrl (imagePath) {
      if (!imagePath) return ''
      // If it's already a full URL, return as is
      if (imagePath.startsWith('http')) return imagePath
      // Otherwise prepend the upload base URL
      return this.uploadUrl + '/' + imagePath.replace(/^\/+/, '')
    },
    getReportRiskLevel (report) {
      if (!report.report_data || !report.report_data.report_data) return 'green'
      const order = ['low', 'medium', 'high']
      let overallHighest = null
      report.report_data.report_data.forEach(item => {
        const levelsForQuestion = []
        if (item.answer === 'yes' && item.risk_level) levelsForQuestion.push(item.risk_level)
        if (item.has_sub_questions && item.sub_questions) {
          item.sub_questions.forEach(subItem => {
            if (subItem.answer === 'yes' && subItem.risk_level) levelsForQuestion.push(subItem.risk_level)
          })
        }
        let questionHighest = null
        if (levelsForQuestion.includes('high')) questionHighest = 'high'
        else if (levelsForQuestion.includes('medium')) questionHighest = 'medium'
        else if (levelsForQuestion.includes('low')) questionHighest = 'low'
        if (questionHighest) {
          if (!overallHighest) overallHighest = questionHighest
          else if (order.indexOf(questionHighest) > order.indexOf(overallHighest)) overallHighest = questionHighest
        }
      })
      return overallHighest || 'green'
    },
    editComment (item) {
      this.editingComment = {
        questionId: item.question_id,
        comment: item.admin_comment || '',
        originalComment: item.user_comment || ''
      }
      this.showEditCommentModal = true
    },
    async saveComment () {
      try {
        const response = await this.$http.put(`risk-report/${this.rowData.id}`, {
          action: 'edit_comment',
          question_id: this.editingComment.questionId,
          admin_comment: this.editingComment.comment
        })
        if (response.status === 200) {
          // Update the local data immediately for UI responsiveness (supports sub-questions)
          if (this.rowData.report_data && this.rowData.report_data.report_data) {
            let updated = false
            for (const q of this.rowData.report_data.report_data) {
              if (q.question_id === this.editingComment.questionId) {
                this.$set(q, 'admin_comment', this.editingComment.comment)
                updated = true
                break
              }
              if (q.sub_questions && Array.isArray(q.sub_questions)) {
                const sub = q.sub_questions.find(sq => sq.question_id === this.editingComment.questionId)
                if (sub) {
                  this.$set(sub, 'admin_comment', this.editingComment.comment)
                  updated = true
                  break
                }
              }
            }
            console.log(updated)
          }

          this.$notify({
            group: 'full',
            text: response.data.msg,
            type: 'success'
          })
          // Emit to direct parent with proper event name
          this.$emit('refresh-risk-reports')
          // Also emit globally for reliability
          this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
        }
      } catch (error) {
        this.$notify({
          group: 'full',
          text: this.$t('Failed to update comment'),
          type: 'error'
        })
      }
    },
    addImages (item) {
      // Use the global modal instead
      this.$root.$emit('open-image-upload-modal', {
        reportId: this.rowData.id,
        questionId: item.question_id,
        callback: (imagePaths) => this.handleImageUploadSuccess(item, imagePaths)
      })
    },
    handleImageUploadSuccess (item, imagePaths) {
      console.log('Image upload success handler called with:', imagePaths, 'for item:', item)
      // Update the local data immediately for UI responsiveness
      if (imagePaths && imagePaths.length > 0) {
        // Initialize images_admin if it doesn't exist
        if (!item.images_admin) {
          this.$set(item, 'images_admin', [])
        }
        const existingImages = item.images_admin || []
        const newImages = [...existingImages, ...imagePaths]
        this.$set(item, 'images_admin', newImages)

        // Emit to direct parent with proper event name
        this.$emit('refresh-risk-reports')
        // Also emit globally for reliability
        this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
      }
    },
    addRiskRow () {
      this.newRiskRow = {
        question_id: Date.now(), // Generate a unique ID
        question_text: '',
        risk_level: 'medium',
        answer: 'no',
        admin_comment: ''
      }
      this.showAddRiskRowModal = true
    },
    editAdditionalRisk () {
      // Initialize editing data from existing additional risk item
      const additionalRisk = this.rowData.report_data.additional_risk_item || {}
      this.editingAdditionalRisk = {
        comment: additionalRisk.comment || '',
        risk_level: additionalRisk.risk_level || null,
        images: additionalRisk.images || []
      }
      this.showEditAdditionalRiskModal = true
    },
    async saveAdditionalRisk () {
      try {
        const response = await this.$http.put(`risk-report/${this.rowData.id}`, {
          action: 'update_additional_risk',
          additional_risk_item: this.editingAdditionalRisk
        })
        if (response.status === 200) {
          // Update the local data immediately for UI responsiveness
          this.$set(this.rowData.report_data, 'additional_risk_item', this.editingAdditionalRisk)

          this.$notify({
            group: 'full',
            text: response.data.msg,
            type: 'success'
          })
          // Emit to direct parent with proper event name
          this.$emit('refresh-risk-reports')
          // Also emit globally for reliability
          this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
        }
      } catch (error) {
        this.$notify({
          group: 'full',
          text: this.$t('Failed to update additional risk'),
          type: 'error'
        })
      } finally {
        this.showEditAdditionalRiskModal = false
      }
    },
    addAdditionalRiskImages () {
      // Use the global modal for image upload
      this.$root.$emit('open-image-upload-modal', {
        reportId: this.rowData.id,
        questionId: 'additional',
        callback: (imagePaths) => this.handleAdditionalRiskImageUploadSuccess(imagePaths)
      })
    },
    handleAdditionalRiskImageUploadSuccess (imagePaths) {
      console.log('Additional risk image upload success handler called with:', imagePaths)
      if (imagePaths && imagePaths.length > 0) {
        // Initialize additional_risk_item if it doesn't exist
        if (!this.rowData.report_data.additional_risk_item) {
          this.$set(this.rowData.report_data, 'additional_risk_item', { images: [] })
        }
        // Initialize images array if it doesn't exist
        if (!this.rowData.report_data.additional_risk_item.images) {
          this.$set(this.rowData.report_data.additional_risk_item, 'images', [])
        }

        const existingImages = this.rowData.report_data.additional_risk_item.images || []
        const newImages = [...existingImages, ...imagePaths]
        this.$set(this.rowData.report_data.additional_risk_item, 'images', newImages)

        // Save to backend
        this.saveAdditionalRiskToBackend()
      }
    },
    async saveAdditionalRiskToBackend () {
      try {
        const response = await this.$http.put(`risk-report/${this.rowData.id}`, {
          action: 'update_additional_risk',
          additional_risk_item: this.rowData.report_data.additional_risk_item
        })
        if (response.status === 200) {
          this.$notify({
            group: 'full',
            text: 'Additional risk images updated successfully',
            type: 'success'
          })
          // Emit refresh events
          this.$emit('refresh-risk-reports')
          this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
        }
      } catch (error) {
        this.$notify({
          group: 'full',
          text: 'Failed to update additional risk images',
          type: 'error'
        })
      }
    },
    async confirmDeleteAdditionalImage (imageIndex) {
      if (!confirm(this.$t('Are you sure you want to delete this image?'))) {
        return
      }

      try {
        // Remove the image from the array
        const updatedImages = [...this.rowData.report_data.additional_risk_item.images]
        updatedImages.splice(imageIndex, 1)

        // Update backend
        const response = await this.$http.put(`risk-report/${this.rowData.id}`, {
          action: 'update_additional_risk',
          additional_risk_item: {
            ...this.rowData.report_data.additional_risk_item,
            images: updatedImages
          }
        })

        if (response.status === 200) {
          // Update local data
          this.$set(this.rowData.report_data.additional_risk_item, 'images', updatedImages)

          this.$notify({
            group: 'full',
            text: this.$t('Image deleted successfully'),
            type: 'success'
          })

          // Emit refresh events
          this.$emit('refresh-risk-reports')
          this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
        }
      } catch (error) {
        this.$notify({
          group: 'full',
          text: this.$t('Failed to delete image'),
          type: 'error'
        })
      }
    },
    async printReport () {
      try {
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = `${this.$store.getters.apiUrl}/printriskreport/${this.rowData.id}`
        form.target = '_blank'

        const tokenInput = document.createElement('input')
        tokenInput.type = 'hidden'
        tokenInput.name = 'token'
        tokenInput.value = this.$auth.token()
        form.appendChild(tokenInput)

        document.body.appendChild(form)
        form.submit()
        document.body.removeChild(form)
      } catch (error) {
        this.$notify({
          group: 'full',
          text: this.$t('Failed to open print report'),
          type: 'error'
        })
      }
    },
    getRiskLevelText (level) {
      const levels = {
        low: this.$t('Low Risk'),
        medium: this.$t('Medium Risk'),
        high: this.$t('High Risk')
      }
      return levels[level] || level
    },
    getAnswerBadgeClass (answer) {
      return ''
    },
    getRiskLevelBadgeClass (level) {
      return {
        'badge-success': level === 'low',
        'badge-warning': level === 'medium',
        'badge-danger': level === 'high'
      }
    },
    openLightbox (image, type, questionId, index) {
      this.lightboxImage = this.getImageUrl(image)
      this.lightboxTitle = `${type === 'user' ? this.$t('User') : this.$t('Admin')} ${this.$t('Image')} ${index + 1}`
      this.lightboxVisible = true
    },
    closeLightbox () {
      this.lightboxVisible = false
      this.lightboxImage = ''
      this.lightboxTitle = ''
    },
    confirmDeleteImage (item, type, index) {
      this.deleteImageData = {
        item: item,
        type: type,
        index: index
      }
      this.showDeleteImageModal = true
    },
    async deleteImage () {
      try {
        const { item, type, index } = this.deleteImageData
        const imageArray = type === 'user' ? item.images_user : item.images_admin
        const imagePath = imageArray[index]

        const response = await this.$http.post(`risk-report/${this.rowData.id}/delete-image`, {
          question_id: item.question_id,
          image_type: type,
          image_path: imagePath
        })

        if (response.status === 200) {
          // Update the local data immediately with proper Vue reactivity
          if (type === 'user') {
            const newArray = [...item.images_user]
            newArray.splice(index, 1)
            this.$set(item, 'images_user', newArray)
          } else {
            const newArray = [...item.images_admin]
            newArray.splice(index, 1)
            this.$set(item, 'images_admin', newArray)
          }

          this.$notify({
            group: 'full',
            text: response.data.msg || this.$t('Image deleted successfully'),
            type: 'success'
          })
          // Emit to direct parent with proper event name
          this.$emit('refresh-risk-reports')
          // Also emit globally for reliability
          this.$root.$emit('risk-reports-changed', { reportId: this.rowData.id })
        }
      } catch (error) {
        this.$notify({
          group: 'full',
          text: (error.response && error.response.data && error.response.data.msg) || this.$t('Failed to delete image'),
          type: 'error'
        })
      }

      // Reset delete data
      this.deleteImageData = {
        item: null,
        type: '',
        index: -1
      }
    }
  }
}
</script>

<style scoped>
.risk-detail-container {
  padding: 1.5rem;
  background-color: #ffffff;
}

.questions-section {
  display: block;
}

.question-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.question-header {
  padding: 1.25rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s ease;
}

.question-header.cursor-pointer {
  cursor: pointer;
}

.question-header.cursor-pointer:hover {
  background: #e9ecef;
}

.question-number {
  background: #6c757d;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.question-text {
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  font-size: 1rem;
}

.question-meta {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.answer-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.question-details {
  padding: 1.25rem;
  background: #fdfdfd;
}

.detail-section {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.detail-content {
  color: #495057;
  line-height: 1.5;
  font-size: 0.9rem;
}


.image-group {
  margin-bottom: 1rem;
}

.image-group:last-child {
  margin-bottom: 0;
}

.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.gallery-image {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid #dee2e6;
}

.gallery-image:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-color: #007bff;
}

.image-container {
  position: relative;
  display: inline-block;
}

.image-delete-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.image-container:hover .image-delete-btn {
  opacity: 1;
}

.image-delete-btn:hover {
  background-color: rgba(220, 53, 69, 1);
  transform: scale(1.05);
}

.admin-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  display: flex;
  gap: 0.75rem;
}

.comments-admin-row {
  margin-top: 1.5rem;
}

.general-comments-card, .admin-tools-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  height: 100%;
}


.admin-tools-grid {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.lightbox-content {
  background-color: #ffffff;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.lightbox-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.lightbox-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
}

.lightbox-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.lightbox-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.lightbox-body {
  padding: 20px;
  text-align: center;
  overflow: auto;
}

.lightbox-image {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .risk-detail-container {
    padding: 1rem;
  }
  
  .question-header {
    padding: 1rem;
  }
  
  .question-details {
    padding: 1rem;
  }
  
  .image-gallery {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
  
  .gallery-image {
    height: 80px;
  }
  
  .admin-tools-grid {
    flex-direction: column;
  }
}

/* Sub-question styles */
.sub-questions-section {
  padding: 1rem 0;
}

.sub-question-card {
  border-left: 3px solid #6c757d;
  background-color: #f8f9fa;
  margin-bottom: 1rem;
  border-radius: 0.25rem;
}

.sub-question-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.sub-question-number {
  background-color: #6c757d;
  color: white;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 2.5rem;
  text-align: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.sub-question-details {
  padding: 0 1rem 1rem;
  background-color: #ffffff;
}
</style>


// WEBPACK FOOTER //
// RiskReportsDetailRow.vue?47b0aaec